// KIQ (Restaurant Intelligence Assistant) — Fase 3: capa de NORMALIZACIÓN de
// datos, no de cálculo. Nunca calcula P&L/food cost/labor/prime cost — toma
// la respuesta YA calculada de un endpoint existente y la reduce a la forma
// predecible que la UI de KIQ necesita, preservando exactamente la misma
// semántica null/0 que el motor financiero ya garantiza (backend/utils/
// pnlHistorico.js:porcentajeSeguro -> null en denominador 0, nunca 0%/
// Infinity/NaN; un mes sin registro -> todos sus campos null, nunca 0
// fabricado). Ningún archivo de este directorio inventa un umbral, una
// recomendación o un insight — eso requiere un motor de reglas validado que
// todavía no existe (ver Fase 3 — reporte final, sección "Future Structured
// Context Contract").
//
// Fuente real reutilizada (nunca duplicada): GET /api/pnl/historico/:anio
// (backend/routes/pnl.js) — el mismo endpoint que ya consume
// frontend/src/pages/PnLHistorico.js. `resumenAnual` ya trae los 6 campos
// que Goals Tracker también usa (mismos nombres de métrica, misma fuente
// canónica: backend/utils/pnlHistorico.js:calcularMetricasFinancieras).
export const METRICAS_BRIEF = ['ventas', 'food_cost_pct', 'labor_pct', 'prime_cost_pct', 'utilidad_operativa', 'margen_operativo_pct'];

function esNumeroValido(v) {
  return v !== null && v !== undefined && Number.isFinite(Number(v));
}

// `respuestaHistorico` es el body completo de GET /api/pnl/historico/:anio.
// Devuelve las 6 métricas SIEMPRE con la misma forma (null si el motor no
// las pudo calcular), nunca clampeadas ni sustituidas por 0. `tieneDatos`
// distingue "el año no tiene ningún mes cargado todavía" (ningún valor real
// en ninguna métrica) de "hay al menos un dato real" — la UI usa esto para
// decidir el estado no_data vs ready, nunca inventando el corte por su cuenta
// campo por campo.
export function normalizarBriefDesdeHistorico(respuestaHistorico) {
  const resumen = respuestaHistorico?.resumenAnual;
  if (!resumen) {
    return { ventas: null, food_cost_pct: null, labor_pct: null, prime_cost_pct: null, utilidad_operativa: null, margen_operativo_pct: null, tieneDatos: false };
  }
  const metricas = {
    ventas: esNumeroValido(resumen.ingresos) ? resumen.ingresos : null,
    food_cost_pct: esNumeroValido(resumen.porcentajes?.foodCost) ? resumen.porcentajes.foodCost : null,
    labor_pct: esNumeroValido(resumen.porcentajes?.labor) ? resumen.porcentajes.labor : null,
    prime_cost_pct: esNumeroValido(resumen.porcentajes?.primeCost) ? resumen.porcentajes.primeCost : null,
    utilidad_operativa: esNumeroValido(resumen.beneficio) ? resumen.beneficio : null,
    margen_operativo_pct: esNumeroValido(resumen.porcentajes?.margenNeto) ? resumen.porcentajes.margenNeto : null,
  };
  const tieneDatos = METRICAS_BRIEF.some((clave) => metricas[clave] !== null);
  return { ...metricas, tieneDatos };
}

// Primer nombre para el saludo (sección 7 del pedido: "Hola, {firstName}",
// nunca hardcodeado). Puramente textual — no toca lógica de negocio.
export function primerNombre(nombreCompleto) {
  if (!nombreCompleto) return null;
  return nombreCompleto.trim().split(/\s+/)[0] || null;
}

// KIQ Intelligence Engine (Fase 4) — normalización defensiva de la
// respuesta de GET /api/kiq/intelligence/:anio. Nunca inventa un insight:
// si la forma no es un arreglo (respuesta inesperada, fallo parcial), se
// degrada a un arreglo vacío — el mismo estado honesto "sin insights" que
// ya existía en Fase 3, nunca un crash ni un dato fabricado.
export function normalizarInsights(respuesta) {
  return Array.isArray(respuesta?.insights) ? respuesta.insights : [];
}
export function normalizarAcciones(respuesta) {
  return Array.isArray(respuesta?.actions) ? respuesta.actions : [];
}

// Severidad -> tratamiento visual (sección 12 del pedido: el color SOLO
// aparece donde el motor ya clasificó explícitamente, nunca inferido de un
// número crudo). `token` es una clave de components/design-system/tokens.js
// status.*; 'info' usa el acento neutral de marca (brand.kiqBlue) en vez de
// status.info para distinguirlo visualmente de una advertencia real.
const SEVERIDAD_VISUAL = {
  critical: { token: 'danger', etiqueta: 'Crítico' },
  warning: { token: 'warning', etiqueta: 'Advertencia' },
  opportunity: { token: 'success', etiqueta: 'Oportunidad' },
  info: { token: 'info', etiqueta: 'Info' },
};
export function visualDeSeveridad(severity) {
  return SEVERIDAD_VISUAL[severity] || SEVERIDAD_VISUAL.info;
}
